import { useEffect } from "react";

type SeoOptions = {
  title: string;
  description: string;
  canonical?: string;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  jsonLdId?: string;
  noindex?: boolean;
};

const upsertMeta = (selector: string, attr: string, key: string, value: string) => {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const useSeo = ({ title, description, canonical, ogType = "website", jsonLd, jsonLdId = "ld-page", noindex = false }: SeoOptions) => {
  useEffect(() => {
    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);

    if (noindex) {
      upsertMeta('meta[name="robots"]', "name", "robots", "noindex, nofollow");
    } else {
      document.head.querySelector('meta[name="robots"]')?.remove();
    }

    const url = canonical ?? (typeof window !== "undefined" ? window.location.href : "");
    if (url) {
      upsertLink("canonical", url);
      upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    }

    if (jsonLd) {
      let el = document.getElementById(jsonLdId) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement("script");
        el.id = jsonLdId;
        el.type = "application/ld+json";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      if (jsonLd) {
        const el = document.getElementById(jsonLdId);
        el?.remove();
      }
      if (noindex) {
        document.head.querySelector('meta[name="robots"]')?.remove();
      }
    };
  }, [title, description, canonical, ogType, jsonLd, jsonLdId, noindex]);
};